import React from "react";
import { Card, Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { useScanQrcodeStore } from "@/store";
import { formatAmountCurrency, isEmpty } from "@/utils";

interface ProductDetailProps {
  className?: string;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ className = "" }) => {
  const { t } = useTranslation();
  const { product, company, stamp_code } = useScanQrcodeStore();

  if (isEmpty(product) || !product?.name) {
    return null;
  }

  return (
    <Card
      className={`${className}`}
      title={t("common.product_info")}
      styles={{ title: { color: "var(--color-warning)", fontWeight: 600 } }}
    >
      <Row gutter={[16, 16]}>
        {product?.name && (
          <Col span={24}>
            <div className="flex justify-between items-center">
              <span className="font-medium max-md:text-sm">{t("common.product_name")}:</span>
              <span className="col-span-7 text-right qrx-text--default font-semibold">{product?.name}</span>
            </div>
          </Col>
        )}

        {product?.brand && (
          <Col span={24}>
            <div className="flex justify-between items-center">
              <span className="font-medium max-md:text-sm">{t("common.brand")}:</span>
              <span className="col-span-7 text-right qrx-text--default font-semibold">{product?.brand}</span>
            </div>
          </Col>
        )}

        {product?.is_show_product_price && (
          <Col span={24}>
            <div className="flex justify-between items-center">
              <span className="font-medium max-md:text-sm">{t("common.product_price")}:</span>
              <span className="col-span-7 text-right qrx-text--default font-semibold">
                {formatAmountCurrency(product?.price, company?.currency)} / {product?.unit}
              </span>
            </div>
          </Col>
        )}
        
        {company?.is_show_internal_notes && !isEmpty(stamp_code.internal_stamp_notes) && (
          <Col span={24}>
            <div className="flex justify-between items-center">
              <span className="font-medium max-md:text-sm">{t("common.internal_notes")}:</span>
              <span className="col-span-7 text-right qrx-text--default font-semibold">{stamp_code.internal_stamp_notes}</span>
            </div>
          </Col>
        )}

        {company?.is_show_stamp_note && !isEmpty(stamp_code.note) && (
          <Col span={24}>
            <div className="flex justify-between items-center">
              <span className="font-medium max-md:text-sm">{t("common.stamp_note")}:</span>
              <span className="col-span-7 text-right qrx-text--default font-semibold">{stamp_code.note}</span>
            </div>
          </Col>
        )}

        {product?.custom_fields &&
          product.custom_fields.length > 0 &&
          product.custom_fields.map((field, index) => (
            <Col span={24} key={index}>
              <div className="flex justify-between items-center">
                <span className="font-medium max-md:text-sm">{field.field_name}:</span>
                <span className="col-span-7 text-right qrx-text--default font-semibold">{field.field_value}</span>
              </div>
            </Col>
          ))}

        {product?.description && product.description.length && (
          <Col span={24}>
            <div className="flex flex-col">
              <span className="font-medium max-md:text-sm mb-2">{t("common.product_description")}:</span>
              <div dangerouslySetInnerHTML={{ __html: product.description }}></div>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default ProductDetail;
